import { XCircle } from "lucide-react";
import { z } from "zod";

import { queryClient } from "@App";
import { i18n } from "@lib/i18n";
import { queries } from "@lib/queries";
import { rootRoute } from "@routes";
import { ResponseError } from "@sdk/spellbook";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import ErrorPage from "../ErrorPage/ErrorPage";

const guestFeedbackTypeSchema = z.enum(["positive", "negative"]);

export type GuestFeedbackType = z.infer<typeof guestFeedbackTypeSchema>;

const guestFeedbackSearchSchema = z.object({
    token: z.coerce.string(),
    type: guestFeedbackTypeSchema.optional(),
    siderId: z.string().optional(),
});

export const guestFeedbackRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: `/guest/feedback/$taskId`,
    component: lazyRouteComponent(() => import("./GuestFeedback")),
    loaderDeps: ({ search: { token } }) => ({ token }),
    loader: ({ params: { taskId }, deps: { token } }) =>
        queryClient.ensureQueryData(queries.guestTask.detail(taskId, token)),
    validateSearch: guestFeedbackSearchSchema,
    beforeLoad: () => {
        return { getTitle: () => i18n.public_feedback_title() };
    },
    errorComponent: ({ error, reset }) => {
        if (error instanceof ResponseError && error.response.status === 403) {
            return <GuestFeedbackForbidden />;
        }
        return <ErrorPage error={error} reset={reset} />;
    },
});

function GuestFeedbackForbidden() {
    return (
        <div className='flex h-screen flex-col items-center justify-center gap-6'>
            <XCircle className='size-8 text-red-500' />
            <div className='flex flex-col items-center gap-2'>
                <h1 className='typography-heading-s-semibold font-medium text-gray-900 md:font-semibold'>
                    {i18n.guest_feedback_forbidden_title()}
                </h1>
                <p className='typography-body-m-regular text-gray-700'>
                    {i18n.guest_feedback_forbidden_subtitle()}
                </p>
            </div>
        </div>
    );
}
